"use strict";

/**
 * SageCodeMappingsController
 *
 * CRUD UI for the `sage_payment_code_mappings` lookup table (WEB-3413 iter-3).
 * The table translates Showsuite payment fields (e.g. payment mode, instrument
 * type) into the Sage codes used in the export CSV — the SAGE Export
 * generation flow joins through this table when emitting customer / GL /
 * payment-mode codes.
 *
 * Backend (post-WEB-3328 iter-22):
 *   GET    /sec/sage-reports/admin/code-mappings    ?property_id=&code_type=
 *   POST   /sec/sage-reports/admin/code-mappings    body: { property_id, source_field, source_value, code_type, sage_code, description? }
 *   PUT    /sec/sage-reports/admin/code-mappings/{id}   body: { property_id, ...editable }
 *   DELETE /sec/sage-reports/admin/code-mappings/{id}   ?property_id=
 *
 * Response envelope (iter-20 standard SSResponse):
 *   GET    → { success, data: { code_mappings: [...] } }
 *   POST/PUT → { success, data: { code_mapping: {...} } }
 *   DELETE → { success, data: { deleted: true, id } }
 *
 * Permission gate (Waseem's iter-16 matrix): `DMS_SAGE_EXPORT_HISTORY_REGENERATE`
 *   — code-mappings deliberately piggybacks on the regenerate permission;
 *   there is NO dedicated code-mappings permission.
 *
 * Globals vs project rows:
 *   The list returns developer-scoped rows merged with global rows
 *   (`developer_id === null`). Globals are read-only — Edit/Delete are
 *   hidden on them and a "Read-only" lock badge is shown instead. The UI
 *   never tries to edit/delete a global row.
 *
 * Project picker: scoped to LISTED projects the user has DMS_VW_PROJ on
 * (server-side via the `status` query param on properties/list, with a
 * defensive client-side filter). `lastPropertyId` is shared across SAGE
 * pages so the selection travels with the user.
 */
class SageCodeMappingsController {

    constructor(SSHttpService, SSAlertService, SSUserService, SSUtilService, SSConfirmationService, SageReportsService, $timeout) {
        const self = this;

        self.SSHttpService = SSHttpService;
        self.SSAlertService = SSAlertService;
        self.SSUserService = SSUserService;
        self.SSUtilService = SSUtilService;
        self.SSConfirmationService = SSConfirmationService;
        self.SageReportsService = SageReportsService;
        self.$timeout = $timeout;

        // Four per-action permissions, per Waseem's iter-26 matrix. The previous
        // single _HISTORY_REGENERATE gate is no longer used for mappings — each
        // CRUD verb now has its own dedicated permission, and the backend
        // enforces the same on the matching /admin/code-mappings endpoint:
        //   VIEW   → GET    /admin/code-mappings        (page access + listing)
        //   ADD    → POST   /admin/code-mappings        (Add Mapping button)
        //   UPDATE → PUT    /admin/code-mappings/{id}   (per-row Edit)
        //   DELETE → DELETE /admin/code-mappings/{id}   (per-row Delete)
        // Super users hold all four implicitly via SSUserService.hasPermission's
        // `user.role === 'SUPER'` short-circuit (services/user.js:426-427).
        self.PERMISSION_VIEW   = 'DMS_SAGE_EXPORT_CODE_MAPPING_VIEW';
        self.PERMISSION_ADD    = 'DMS_SAGE_EXPORT_CODE_MAPPING_ADD';
        self.PERMISSION_UPDATE = 'DMS_SAGE_EXPORT_CODE_MAPPING_UPDATE';
        self.PERMISSION_DELETE = 'DMS_SAGE_EXPORT_CODE_MAPPING_DELETE';

        self.has_access = self.SSUserService.hasPermission(self.PERMISSION_VIEW);
        self.canAdd     = self.SSUserService.hasPermission(self.PERMISSION_ADD);
        self.canUpdate  = self.SSUserService.hasPermission(self.PERMISSION_UPDATE);
        self.canDelete_ = self.SSUserService.hasPermission(self.PERMISSION_DELETE);
        // Backward-compat alias used by older Blade markup — true when the
        // user holds *any* mutating permission. Each row's per-action helper
        // (canEdit / canDelete) re-checks the specific verb permission below.
        self.canManage  = self.canAdd || self.canUpdate || self.canDelete_;

        // Valid mapping field/type options (WEB-3622: removed dead bank_code/property_account_type)
        self.SOURCE_FIELD_OPTIONS = [
            { value: 'instrument_type', label: 'Instrument Type' },
            { value: 'payment_type', label: 'Payment Type' }
        ];

        self.CODE_TYPE_OPTIONS = [
            { value: 'payment_code', label: 'Payment Code (receipts)' },
            { value: 'distribution_code', label: 'Distribution Code (invoices)' }
        ];

        self.properties = null;
        self.selectedPropertyId = null;
        self.filters = self._defaultFilters();
        self.rows = [];
        self.loading = false;
        self.searched = false;

        // Modal state — single modal element in the Blade, toggled open via
        // `toggleEditContainer` boolean. `entry` carries the row currently
        // being created (no id) or edited (has id). `errorMessage` surfaces
        // a backend error inside the modal without dismissing it.
        self.toggleEditContainer = false;
        self.entry = null;
        self.errorMessage = '';
        self.saving = false;

        if (self.has_access) {
            self.init();
        }
    }

    init() {
        const self = this;
        self.prepareDeveloperProperties();
    }

    _defaultFilters() {
        return {
            code_type: ''
        };
    }

    _emptyEntry() {
        return {
            id: null,
            source_field: '',
            source_value: '',
            code_type: '',
            sage_code: '',
            description: ''
        };
    }

    /**
     * Fetch the user's permitted LISTED projects. Same pattern as history.js
     * — server-side `status` filter plus a defensive client-side filter, and
     * the `lastPropertyId` cross-page memory key.
     */
    prepareDeveloperProperties() {
        const self = this;

        let params = { status: SHOWSUITE.PROPERTY_STATUS_LISTED };

        loading(true);
        self.SSHttpService.getAPIRequest('sec/developer/properties/list', params).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }
            let all = (response.data && response.data.properties) || [];
            self.properties = all.filter(function (p) {
                return p && p.status === SHOWSUITE.PROPERTY_STATUS_LISTED;
            });

            let saved = self.SSUtilService.getFromLocalStorage('lastPropertyId');
            if (saved && self.properties.some(p => String(p.id) === String(saved))) {
                self.selectedPropertyId = parseInt(saved, 10);
            }

            self.$timeout(function () {
                if ($('select.dashboard-select#property-select').niceSelect) {
                    $('select.dashboard-select#property-select').niceSelect('update');
                }
            });
        });
    }

    // -- View handlers -------------------------------------------------------

    onPropertyChange() {
        const self = this;
        self._resetResults();
        if (self.selectedPropertyId) {
            self.SSUtilService.saveToLocalStorage('lastPropertyId', self.selectedPropertyId);
        }
    }

    _resetResults() {
        const self = this;
        self.rows = [];
        self.searched = false;
    }

    /**
     * User-triggered search. Project required; code_type optional.
     */
    search() {
        const self = this;
        if (!self.selectedPropertyId) {
            self.SSAlertService.danger('Validation Error', 'Please select a project first.');
            return;
        }
        self.fetch();
    }

    fetch() {
        const self = this;

        if (!self.selectedPropertyId) { return; }

        let params = { property_id: self.selectedPropertyId };
        if (self.filters.code_type) { params.code_type = self.filters.code_type; }

        self.loading = true;
        loading(true);

        self.SageReportsService.listCodeMappings(params).then(function (response) {
            loading(false);
            self.loading = false;
            self.searched = true;

            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            // Iter-20 envelope: response.data is { code_mappings: [...] }.
            // Fall back to response.data when the backend hasn't wrapped yet
            // (rare — but matches the defensive shape detection in history.js).
            let inner = (response && response.data) || {};
            self.rows = inner.code_mappings || (Array.isArray(inner) ? inner : []);
        });
    }

    // -- Row classification --------------------------------------------------

    /**
     * Get filtered code type options based on selected source field.
     * Only show the valid code type for the selected source field (WEB-3622).
     */
    getCodeTypeOptionsForSourceField() {
        const self = this;
        if (!self.entry || !self.entry.source_field) {
            return self.CODE_TYPE_OPTIONS;
        }

        switch (self.entry.source_field) {
            case 'instrument_type':
                return self.CODE_TYPE_OPTIONS.filter(opt => opt.value === 'payment_code');
            case 'payment_type':
                return self.CODE_TYPE_OPTIONS.filter(opt => opt.value === 'distribution_code');
            default:
                return self.CODE_TYPE_OPTIONS;
        }
    }

    /**
     * A mapping is global when developer_id is NULL — these are shipped with
     * the platform and apply to every developer that doesn't have a project-
     * specific override. The UI must never allow edit/delete on a global,
     * even if the backend would accept it.
     */
    isGlobal(row) {
        if (!row) { return false; }
        return row.developer_id === null || typeof row.developer_id === 'undefined';
    }

    // Per-row gates use the specific verb permission (iter-26). Global rows
    // (developer_id === null) stay read-only — the UI never offers Edit /
    // Delete on a global even if the backend would accept it.
    canEdit(row)   { return this.canUpdate  && !this.isGlobal(row); }
    canDelete(row) { return this.canDelete_ && !this.isGlobal(row); }

    // -- Modal: Add / Edit ---------------------------------------------------

    /**
     * Toggle the Bootstrap modal — Bootstrap's `.modal.fade` is `display:none`
     * by default and only becomes visible when the jQuery `.modal('show')`
     * plugin is invoked (it adds the `.show` class, the backdrop, and the
     * required ARIA attributes). The `ng-show="ctrl.toggleEditContainer"`
     * binding on the modal element is decorative — it keeps the boolean
     * around for other directives but does not actually show the modal on
     * its own. Same pattern as developer/mapping/mapping.js, payment_details.js
     * etc — see those for precedent.
     */
    _showModal(show) {
        this.toggleEditContainer = !!show;
        jQuery('#sageCodeMappingEditContainer').modal(show ? 'show' : 'hide');
    }

    openAdd() {
        const self = this;
        if (!self.canAdd) { return; }
        if (!self.selectedPropertyId) {
            self.SSAlertService.danger('Validation Error', 'Please select a project first.');
            return;
        }
        self.entry = self._emptyEntry();
        self.errorMessage = '';
        self._showModal(true);
    }

    openEdit(row) {
        const self = this;
        if (!self.canEdit(row)) { return; }
        // Shallow clone so the table row doesn't update live while the user
        // is still typing — only commit changes back on save.
        self.entry = {
            id: row.id,
            source_field: row.source_field || '',
            source_value: row.source_value || '',
            code_type: row.code_type || '',
            sage_code: row.sage_code || '',
            description: row.description || ''
        };
        self.errorMessage = '';
        self._showModal(true);
    }

    closeModal() {
        const self = this;
        self._showModal(false);
        self.entry = null;
        self.errorMessage = '';
    }

    /**
     * Client-side validation before hitting the backend. The backend has
     * the authoritative rules — this only catches obvious empties and invalid
     * combinations so the user gets feedback without a network round-trip.
     */
    _validateEntry() {
        const self = this;
        if (!self.entry) { return 'No entry to save.'; }
        if (!self.entry.source_field || !self.entry.source_field.trim()) { return 'Source Field is required.'; }
        if (!self.entry.source_value || !self.entry.source_value.trim()) { return 'Source Value is required.'; }
        if (!self.entry.code_type || !self.entry.code_type.trim()) { return 'Code Type is required.'; }
        if (!self.entry.sage_code || !self.entry.sage_code.trim()) { return 'Sage Code is required.'; }

        // Validate mapping combinations (WEB-3622): only two valid combos exist
        const validCombinations = [
            { source_field: 'instrument_type', code_type: 'payment_code' },
            { source_field: 'payment_type', code_type: 'distribution_code' }
        ];

        const isValidCombo = validCombinations.some(combo =>
            self.entry.source_field === combo.source_field &&
            self.entry.code_type === combo.code_type
        );

        if (!isValidCombo) {
            return 'Invalid mapping combination. Valid combinations: ' +
                   'instrument_type → payment_code (receipts), ' +
                   'payment_type → distribution_code (invoices).';
        }

        return null;
    }

    save() {
        const self = this;
        if (self.saving) { return; }

        let err = self._validateEntry();
        if (err) { self.errorMessage = err; return; }

        let payload = {
            property_id: self.selectedPropertyId,
            source_field: self.entry.source_field.trim(),
            source_value: self.entry.source_value.trim(),
            code_type: self.entry.code_type.trim(),
            sage_code: self.entry.sage_code.trim(),
            description: (self.entry.description || '').trim()
        };

        self.saving = true;
        loading(true);

        let request = self.entry.id
            ? self.SageReportsService.updateCodeMapping(self.entry.id, payload)
            : self.SageReportsService.createCodeMapping(payload);

        request.then(function (response) {
            loading(false);
            self.saving = false;

            if (response instanceof Error) {
                // Surface inside the modal so the user can fix and retry.
                self.errorMessage = response.message || 'Could not save the mapping.';
                return;
            }

            self.SSAlertService.success(
                self.entry.id ? 'Mapping updated' : 'Mapping created',
                self.entry.id
                    ? 'The code mapping has been updated.'
                    : 'A new code mapping has been created for this project.'
            );
            self.closeModal();
            self.fetch();
        });
    }

    // -- Delete --------------------------------------------------------------

    confirmDelete(row) {
        const self = this;
        if (!self.canDelete(row)) { return; }

        self.SSConfirmationService.getConfirmed(
            'Delete code mapping?',
            'This will remove the project-specific mapping for "' + (row.source_field || '') +
                '" → "' + (row.source_value || '') + '" (' + (row.code_type || '') + '). ' +
                'Future SAGE exports will fall back to the global mapping (if any).',
            self.SSConfirmationService.TYPE_DEFAULT,
            function () { self._doDelete(row); }
        );
    }

    _doDelete(row) {
        const self = this;

        loading(true);
        self.SageReportsService.deleteCodeMapping(row.id, self.selectedPropertyId).then(function (response) {
            loading(false);

            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            self.SSAlertService.success(
                'Mapping deleted',
                'The code mapping has been removed.'
            );
            self.fetch();
        });
    }
}

SageCodeMappingsController.$inject = [
    'SSHttpService',
    'SSAlertService',
    'SSUserService',
    'SSUtilService',
    'SSConfirmationService',
    'SageReportsService',
    '$timeout'
];
app.controller('SageCodeMappingsController', SageCodeMappingsController);
